/* eslint-disable import/prefer-default-export */
import { ref, computed, watch } from 'vue';
import { provideLocal, toValue } from '@vueuse/core';

import { FREQUENCY_KEY_REPOSITORY, SHIPS } from '../constants';

const SHIPS_VALUES = Object.values(SHIPS);

/**
 * * This composable is used to manage the auto replenishment feature
 *
 * @desription
 * This composable is provider for the useAutoReplenishment composable.
 * See explanation in useAutoReplenishment.js or Readme.md files.
 * {@link file://./useAutoReplenishment.js}
 * {@link file://./Readme.md}
 *
 * @param {import('@vueuse/core').MaybeRefOrGetter<boolean>} autoReplenishmentEnabledFlag
 */
export const useAutoReplenishmentProvide = (autoReplenishmentEnabledFlag = false) => {
  /**
   * @type {import('vue').Ref<SHIPS>}
   */
  const frequency = ref(SHIPS.NONE);
  const autoReplenishmentEnabled = ref(false);
  const frequencySelected = computed(
    () => toValue(autoReplenishmentEnabledFlag)
      && autoReplenishmentEnabled.value
      && !!frequency.value
      && frequency.value !== SHIPS.NONE,
  );

  /**
   * @param {SHIPS} newValue
   */
  const updateFrequency = (newValue) => {
    if (!toValue(autoReplenishmentEnabledFlag)) {
      // eslint-disable-next-line no-console
      console.warn('😕 Auto replenishment is not enabled.\n%cfrequency will not be updated', 'color: red');
      return;
    }
    if (!SHIPS_VALUES.includes(newValue)) {
      throw new TypeError(`Invalid frequency value: ${newValue}`);
    }
    frequency.value = newValue;
  };

  /**
   * @param {boolean} newValue
   */
  const updateAutoReplenishmentEnabled = (newValue) => {
    if (!toValue(autoReplenishmentEnabledFlag)) {
      // eslint-disable-next-line no-console
      console.warn('😕 Auto replenishment is not enabled.\n%cautoReplenishmentEnabled will not be updated', 'color: red');
      return;
    }
    autoReplenishmentEnabled.value = newValue;
  };

  const frequencyRef = computed({
    get: () => frequency.value,
    set: updateFrequency,
  });

  const autoReplenishmentEnabledRef = computed({
    get: () => autoReplenishmentEnabled.value,
    set: updateAutoReplenishmentEnabled,
  });

  watch(autoReplenishmentEnabled, (newValue) => {
    if (!newValue) {
      frequency.value = SHIPS.NONE;
    }
  });

  provideLocal(FREQUENCY_KEY_REPOSITORY, {
    autoReplenishmentEnabled: autoReplenishmentEnabledRef,
    frequency: frequencyRef,
    frequencySelected,
  });
};
